import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  MessageSquare, Clock, CheckCircle, AlertCircle,
  RefreshCw, Send, X, User, ChevronDown, Flag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format, isValid } from "date-fns";

const fmtDate = (value, pattern) => {
  const d = new Date(value);
  return value && isValid(d) ? format(d, pattern) : "—";
};
import AdminLayout from "@/components/AdminLayout";

const STATUS_CONFIG = {
  open: { label: "Open", color: "bg-blue-100 text-blue-700" },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700" },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-700" },
  closed: { label: "Closed", color: "bg-gray-100 text-gray-700" },
};

const PRIORITY_CONFIG = {
  low: { label: "Low", color: "bg-gray-100 text-gray-700" },
  medium: { label: "Medium", color: "bg-blue-100 text-blue-700" },
  high: { label: "High", color: "bg-orange-100 text-orange-700" },
  urgent: { label: "Urgent", color: "bg-red-100 text-red-700" },
};

const CATEGORIES = {
  account: { label: "Account", icon: "👤" },
  technical: { label: "Technical", icon: "🔧" },
  billing: { label: "Billing", icon: "💳" },
  safety: { label: "Safety", icon: "🛡️" },
  other: { label: "Other", icon: "📝" },
};

const AdminSupport = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({ status: "", priority: "", category: "" });

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [filters]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await api.getAdminTickets(filters.status, filters.priority, filters.category);
      setTickets(response.data);
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.getSupportStats();
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  const handleUpdateTicket = async (ticketId, update) => {
    try {
      await api.updateAdminTicket(ticketId, update);
      toast.success("Ticket updated");
      fetchTickets();
      fetchStats();
      if (selectedTicket?.id === ticketId) {
        const response = await api.getAdminTicket(ticketId);
        setSelectedTicket(response.data);
      }
    } catch (error) {
      toast.error("Failed to update ticket");
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;

    setSubmitting(true);
    try {
      await api.adminReplyToTicket(selectedTicket.id, replyText);
      toast.success("Reply sent!");
      setReplyText("");
      const response = await api.getAdminTicket(selectedTicket.id);
      setSelectedTicket(response.data);
      fetchTickets();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSubmitting(false);
    }
  };

  const openTickets = stats?.by_status?.open || 0;
  const inProgressTickets = stats?.by_status?.in_progress || 0;

  return (
    <AdminLayout>
    <div className="bg-gray-50" data-testid="admin-support-page">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4 sticky top-[52px] z-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-[#1C3A7A]">Support Tickets</h1>
              <p className="text-sm text-gray-500">{openTickets + inProgressTickets} active tickets</p>
            </div>
            <Button onClick={fetchTickets} variant="outline" size="sm">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="bg-blue-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{stats.by_status?.open || 0}</p>
                <p className="text-xs text-blue-600">Open</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{stats.by_status?.in_progress || 0}</p>
                <p className="text-xs text-amber-600">In Progress</p>
              </div>
              <div className="bg-green-50 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{stats.by_status?.resolved || 0}</p>
                <p className="text-xs text-green-600">Resolved</p>
              </div>
              <div className="bg-gray-100 rounded-xl p-3 text-center">
                <p className="text-2xl font-bold text-gray-700">{stats.total || 0}</p>
                <p className="text-xs text-gray-600">Total</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm bg-white"
            >
              <option value="">All Status</option>
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm bg-white"
            >
              <option value="">All Priority</option>
              {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm bg-white"
            >
              <option value="">All Categories</option>
              {Object.entries(CATEGORIES).map(([key, val]) => (
                <option key={key} value={key}>{val.icon} {val.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FAB710] border-t-transparent" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900">No tickets found</h3>
            <p className="text-sm text-gray-500">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Ticket</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 text-sm">{ticket.subject}</p>
                      <p className="text-xs text-gray-500">{ticket.ticket_number}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900">{ticket.user_name}</p>
                      <p className="text-xs text-gray-500">{ticket.user_email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm">{CATEGORIES[ticket.category]?.icon} {CATEGORIES[ticket.category]?.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_CONFIG[ticket.status]?.color}`}>
                        {STATUS_CONFIG[ticket.status]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${PRIORITY_CONFIG[ticket.priority]?.color}`}>
                        {PRIORITY_CONFIG[ticket.priority]?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {fmtDate(ticket.created_at, "MMM d")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-[#1C3A7A] px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-white font-semibold">{selectedTicket.subject}</h3>
                <p className="text-white/70 text-xs">{selectedTicket.ticket_number} • {selectedTicket.user_name}</p>
              </div>
              <button onClick={() => setSelectedTicket(null)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Controls */}
            <div className="px-4 py-3 border-b bg-gray-50 flex gap-2 flex-wrap flex-shrink-0">
              <select
                value={selectedTicket.status}
                onChange={(e) => handleUpdateTicket(selectedTicket.id, { status: e.target.value })}
                className="px-3 py-1.5 border rounded-lg text-sm bg-white"
              >
                {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
              <select
                value={selectedTicket.priority}
                onChange={(e) => handleUpdateTicket(selectedTicket.id, { priority: e.target.value })}
                className="px-3 py-1.5 border rounded-lg text-sm bg-white"
              >
                {Object.entries(PRIORITY_CONFIG).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500 flex items-center gap-1">
                {CATEGORIES[selectedTicket.category]?.icon} {CATEGORIES[selectedTicket.category]?.label}
              </span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Original message */}
              <div className="bg-gray-100 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">{selectedTicket.user_name}</span>
                </div>
                <p className="text-sm text-gray-900">{selectedTicket.description}</p>
                <p className="text-xs text-gray-500 mt-2">
                  {fmtDate(selectedTicket.created_at, "MMM d, yyyy 'at' h:mm a")}
                </p>
              </div>

              {/* Replies */}
              {selectedTicket.replies?.map((reply) => (
                <div
                  key={reply.id}
                  className={`rounded-xl p-3 ${
                    reply.sender_type === "admin"
                      ? "bg-[#1C3A7A] text-white ml-8"
                      : "bg-gray-100 mr-8"
                  }`}
                >
                  <p className={`text-xs font-medium mb-1 ${reply.sender_type === "admin" ? "text-white/70" : "text-gray-500"}`}>
                    {reply.sender_name}
                  </p>
                  <p className="text-sm">{reply.message}</p>
                  <p className={`text-xs mt-2 ${reply.sender_type === "admin" ? "text-white/50" : "text-gray-500"}`}>
                    {fmtDate(reply.created_at, "MMM d 'at' h:mm a")}
                  </p>
                </div>
              ))}
            </div>

            {/* Reply Input */}
            {selectedTicket.status !== "closed" && (
              <div className="p-4 border-t bg-white flex-shrink-0">
                <div className="flex gap-2">
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1"
                    onKeyPress={(e) => e.key === "Enter" && handleReply()}
                  />
                  <Button onClick={handleReply} disabled={!replyText.trim() || submitting} className="bg-[#FAB710]">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminSupport;
